import React from "react";
import { Box, Tooltip } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { MoreHorizIcon } from "../../../styles/StyledMUI";

export type MotifCellProps = {
  text: string;
  hasReason: boolean;
  /** Optional callback; if provided and content is truncated, an icon button opens the full content. */
  onOpen?: () => void;
  maxWidth?: number | string;
};

export default function MotifCell({ text, hasReason, onOpen, maxWidth = 380 }: MotifCellProps) {
  const textRef = React.useRef<HTMLElement | null>(null);
  const [isOverflowed, setIsOverflowed] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!hasReason) {
      setIsOverflowed(false);

      return;
    }

    const node = textRef.current;

    if (!node) return;

    const update = () => {
      setIsOverflowed(node.scrollHeight > node.clientHeight + 1);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [text, hasReason]);

  const showOpenIcon = hasReason && isOverflowed && !!onOpen;
  // Native tooltip fallback when truncated but no modal handler is wired.
  const fallbackTitle = hasReason && isOverflowed && !onOpen ? text : undefined;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, maxWidth }}>
      <DarkTypography
        ref={textRef}
        level="body-sm"
        title={fallbackTitle}
        sx={{
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {text}
      </DarkTypography>
      {showOpenIcon && (
        <Tooltip title="Voir le motif complet">
          <IconButtonCTA
            aria-label="Voir le motif complet"
            ctaVariant="accent"
            size="sm"
            onClick={onOpen}
          >
            <MoreHorizIcon />
          </IconButtonCTA>
        </Tooltip>
      )}
    </Box>
  );
}
